import type { EnvironmentId } from "@cmmd-forge/contracts";
import { useState } from "react";
import { resolveEnvironmentHttpUrl } from "../environments/runtime";
import { cn } from "~/lib/utils";
import { ProjectLetterAvatar } from "./ProjectLetterAvatar";

const loadedProjectFaviconSrcs = new Set<string>();

/**
 * The favicon URL, or "" when this render cannot address the environment.
 *
 * Resolution reads the primary-environment target, which does not exist on the
 * server and can be absent mid-boot. Letting that throw would take the whole
 * app down through the nearest boundary for the sake of a 14px glyph, so a
 * failure degrades to the letter avatar instead.
 */
function resolveProjectFaviconSrc(environmentId: EnvironmentId, cwd: string): string {
  try {
    return resolveEnvironmentHttpUrl({
      environmentId,
      pathname: "/api/project-favicon",
      searchParams: { cwd },
    });
  } catch {
    return "";
  }
}

export function ProjectFavicon(input: {
  environmentId: EnvironmentId;
  cwd: string;
  /**
   * Project name used for the fallback letter-avatar when the favicon is
   * missing/failed or there is no cwd to fetch from. Omit to keep the plain
   * placeholder glyph.
   */
  projectName?: string | null;
  className?: string | undefined;
  fallbackOnly?: boolean;
}) {
  // An empty cwd (e.g. a pre-connect cloud project with no VM-local path) has no
  // favicon to fetch — requesting /api/project-favicon?cwd= just 400s. Skip the
  // remote load and render the fallback directly.
  const src =
    input.fallbackOnly === true || input.cwd.trim() === ""
      ? ""
      : resolveProjectFaviconSrc(input.environmentId, input.cwd);
  const shouldLoadRemote = src !== "";
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(() =>
    shouldLoadRemote && loadedProjectFaviconSrcs.has(src) ? "loaded" : "loading",
  );

  return (
    <>
      {!shouldLoadRemote || status !== "loaded" ? (
        // Favicon missing, failed, or still loading → a project-specific
        // letter-avatar (or a glyph when no name is provided). Once the real
        // favicon loads it replaces this; the src cache makes that one-time.
        <ProjectLetterAvatar projectName={input.projectName ?? null} className={input.className} />
      ) : null}
      {shouldLoadRemote ? (
        <img
          src={src}
          alt=""
          className={cn(
            "size-3.5 shrink-0 rounded-sm object-contain",
            status === "loaded" ? "" : "hidden",
            input.className,
          )}
          onLoad={() => {
            loadedProjectFaviconSrcs.add(src);
            setStatus("loaded");
          }}
          onError={() => setStatus("error")}
        />
      ) : null}
    </>
  );
}
